<template>
  <div class="card">
    <div class="card-header">
      <div class="header-icon">
        <i class="fa-solid fa-receipt"></i>
      </div>
      <div class="header-content">
        <h3 class="card-title">New Transaction</h3>
        <p class="card-subtitle">Describe your expense naturally</p>
      </div>
    </div>

    <div class="card-body">
      <!-- Natural Language Input -->
      <div class="natural-input-section">
        <label for="naturalInput" class="form-label fw-semibold">
          <i class="fa-solid fa-comment-dots me-1"></i>Tell me about your transaction
        </label>
        <div class="input-container">
          <input
            id="naturalInput"
            ref="naturalInput"
            v-model="naturalDescription"
            type="text"
            class="form-control form-control-lg natural-input"
            placeholder="e.g., 'I spent $45 at Whole Foods for groceries today'"
            @input="handleInput"
            @keydown="handleKeydown"
          />
          <div class="input-footer">
            <small class="text-muted">
              <i class="fa-solid fa-lightbulb me-1"></i>
              Describe amounts, places, and what you bought - I'll organize it for you
            </small>
          </div>
        </div>
      </div>

      <!-- Preview Section (for future use) -->
      <div v-if="parsedTransactions.length > 0" class="mt-3">
        <h6 class="preview-title">
          <i class="fa-solid fa-eye me-1"></i>Preview
        </h6>
        <div class="preview-section">
          <div class="preview-transactions">
            <div 
              v-for="(transaction, index) in previewTransactions" 
              :key="index"
              class="preview-transaction"
              :class="{ 
                'expense-preview': transaction.type === 'debit', 
                'income-preview': transaction.type === 'credit',
                'savings-preview': transaction.type === 'savings'
              }"
            >
              <div class="preview-content">
                <div class="preview-description">{{ transaction.description }}</div>
                <div class="preview-details">
                  <span class="preview-amount">
                    <span v-if="transaction.type === 'debit'">-</span>${{ transaction.amount.toFixed(2) }}
                  </span>
                  <span v-if="transaction.category" class="preview-category">
                    <i class="fa-solid fa-tag me-1"></i>{{ transaction.category }}
                  </span>
                  <span v-if="transaction.date" class="preview-date">
                    <i class="fa-solid fa-calendar me-1"></i>{{ formatDate(transaction.date) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Button -->
      <div class="action-section mt-4 pt-3">
        <button 
          v-if="parsedTransactions.length === 0"
          @click="processTransaction" 
          class="btn btn-primary btn-lg w-100"
          :disabled="!naturalDescription.trim() || isProcessing"
        >
          <span v-if="isProcessing" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          <i v-else class="fa-solid fa-wand-magic-sparkles me-2"></i>
          <span v-if="isProcessing">Processing...</span>
          <span v-else>Process Transaction</span>
        </button>
        
        <div v-else class="d-flex gap-3">
          <button 
            @click="confirmTransactions" 
            class="btn btn-primary btn-lg flex-fill"
          >
            <i class="fa-solid fa-check me-2"></i>
             {{ parsedTransactions.length }} Transaction{{ parsedTransactions.length > 1 ? 's' : '' }}
          </button>
          
          <button 
            @click="dismissPreview" 
            class="btn btn-secondary btn-lg"
          >
            <i class="fa-solid fa-trash me-2"></i>
            Discard
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { parseTransactionDescription } from '../utils/transactionService';
import { useAuth } from '../composables/useAuth';
import { useErrorModal } from '../composables/useErrorModal';
import { useCategoryKeywords } from '../composables/useCategoryKeywords';

export default {
  name: 'NaturalLanguageTransaction',
  props: {
    autoConfirm: {
      type: Boolean,
      default: false
    }
  },
  emits: ['transaction-processed', 'transactions-confirmed', 'processing-started'],
  setup() {
    const { getAccessToken } = useAuth();
    const { showApiError } = useErrorModal();
    const { autoCategorize, loadKeywords } = useCategoryKeywords();
    return { getAccessToken, showApiError, autoCategorize, loadKeywords };
  },
  data() {
    return {
      naturalDescription: '',
      isProcessing: false,
      parsedTransactions: []
    }
  },
  computed: {
    // Apply the same category prioritization logic as in App.vue
    previewTransactions() {
      return this.parsedTransactions.map(transaction => {
        if (transaction.description) {
          // Check for local category rule first
          const localCategory = this.autoCategorize(transaction.description);
          if (localCategory) {
            // Override API category with local rule
            return { ...transaction, category: localCategory };
          } else if (transaction.category) {
            // Keep API category if no local rule exists
            return { ...transaction };
          }
          // No category from either source
          return { ...transaction, category: '' };
        }
        return transaction;
      });
    }
  },
  methods: {
    handleInput() {

    },

    handleKeydown(event) {
      // Allow Ctrl/Cmd + Enter to submit
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault();
        this.processTransaction();
      }
    },

    async processTransaction() {
      if (!this.naturalDescription.trim() || this.isProcessing) return;

      this.isProcessing = true;
      
      // Emit event to show toast notification instead of full-screen loading
      this.$emit('processing-started', {
        description: this.naturalDescription.trim()
      });
      
      try {
        // Get access token for authenticated API request
        const accessToken = await this.getAccessToken();
        
        // Use current local date as reference for relative date parsing (e.g., "yesterday", "today")
        // Format as YYYY-MM-DD in local timezone to avoid UTC date shift issues
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const referenceDate = `${year}-${month}-${day}`;
        
        // Call the transaction parsing API
        const apiResult = await parseTransactionDescription(
          this.naturalDescription.trim(),
          accessToken,
          referenceDate
        );

        // Extract parsed transactions from API response
        const parsedTransactions = apiResult.data.transactions || [];
        
        // Store parsed transactions for preview
        this.parsedTransactions = parsedTransactions;
        
        // Emit the parsed transactions to parent component for preview
        this.$emit('transaction-processed', {
          naturalDescription: this.naturalDescription.trim(),
          parsedTransactions: parsedTransactions,
          timestamp: new Date().toISOString()
        });

        // If auto-confirm is enabled, immediately confirm transactions
        if (this.autoConfirm) {
          this.confirmTransactions();
        }

        // Don't clear the input yet - wait for confirmation

      } catch (error) {
        console.error('Error processing natural language transaction:', error);
        this.showApiError(error);
      } finally {
        this.isProcessing = false;
      }
    },

    confirmTransactions() {
      // Emit confirmation event with the processed transactions (with final categories)
      this.$emit('transactions-confirmed', {
        naturalDescription: this.naturalDescription.trim(),
        parsedTransactions: this.previewTransactions, // Use processed transactions with final categories
        timestamp: new Date().toISOString()
      });

      // Clear the input and preview after confirmation
      this.naturalDescription = '';
      this.parsedTransactions = [];
    },

    dismissPreview() {
      // Clear the preview without confirming
      this.parsedTransactions = [];
    },

    formatDate(dateString) {
      try {
        // Add noon time to avoid timezone issues, same as in App.vue
        const date = new Date(`${dateString}T12:00:00`);
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
      } catch {
        return dateString;
      }
    }
  },
  async mounted() {
    // Load keywords when component mounts to ensure autoCategorize works
    try {
      const accessToken = await this.getAccessToken();
      if (accessToken) {
        await this.loadKeywords(accessToken);
      }
    } catch (error) {
      console.error('Failed to load keywords in NaturalLanguageTransaction:', error);
    }
  }
}
</script>

<style scoped>
/* Natural Input Section */
.natural-input-section .form-label {
  color: var(--light-text);
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
}

.input-container {
  position: relative;
}

.natural-input {
  border-radius: 0.5rem;
  border: 2px solid var(--light-border);
  transition: all 0.2s ease;
  background-color: var(--light-card);
  color: var(--light-text);
  height: 50px;
  font-size: 16px;
}

.natural-input:focus {
  background-color: var(--light-input-bg);
  border-color: var(--light-text);
  color: var(--light-text);
  box-shadow: none;
}

.natural-input::placeholder {
  color: var(--light-text-muted);
  opacity: 0.7;
}

.input-footer {
  margin-top: 0.5rem;
}

.input-footer small {
  color: var(--light-text-muted);
  font-size: 0.8rem;
}

/* Preview Section */
.preview-title {
  color: var(--light-text);
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.preview-section {
  background: var(--nested-container-bg);
  border-radius: 8px;
  padding: 1rem;
  border: 2px solid var(--light-border);
}

.preview-transactions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.preview-transaction {
  background: var(--light-card);
  border-radius: 6px;
  padding: 0.75rem;
  border-left: 3px solid var(--light-border);
}

.preview-transaction.expense-preview {
  border-left-color: var(--light-danger);
}

.preview-transaction.income-preview {
  border-left-color: var(--light-success);
}

.preview-transaction.savings-preview {
  border-left-color: #36A2EB;
}

.preview-transaction.income-preview {
  border-left-color: var(--light-success);
}

.preview-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.preview-description {
  color: var(--light-text);
  font-weight: 500;
  flex: 1;
}

.preview-details {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.preview-amount {
  font-weight: 600;
  font-size: 0.9rem;
}

.expense-preview .preview-amount {
  color: var(--light-danger);
}

.income-preview .preview-amount {
  color: var(--light-success);
}

.savings-preview .preview-amount {
  color: #36A2EB;
}

.income-preview .preview-amount {
  color: var(--light-success);
}

.preview-category {
  color: var(--light-text-muted);
  font-size: 0.8rem;
  background: var(--nested-container-bg);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.preview-date {
  color: var(--light-text-muted);
  font-size: 0.8rem;
  background: var(--nested-container-bg);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

/* Action Section */
.action-section {
  border-top: 1px solid var(--light-border);
}

.btn-primary {
  background-color: #36A2EB;
  border-color: #36A2EB;
  min-height: 50px;
  color: white;
}

.btn-primary:hover {
  background-color: #2e8bc7;
  border-color: #2e8bc7;
}

.btn-primary:disabled {
  background-color: var(--light-text-muted);
  border-color: var(--light-text-muted);
  opacity: 0.6;
}

/* Responsive Design */
@media (max-width: 768px) {
  .natural-input {
    font-size: 16px;
  }

  .preview-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .preview-details {
    width: 100%;
    justify-content: space-between;
  }
}
</style>